import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { settingsService } from 'v2/shared/services/settings-service'
import { getAPIConsoleQueryKeys } from 'v2/shared/utils/query-key-management'
import type { ROriginationURIInternal } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import { errorToast } from 'v2/utils/toasts'
import {
  type EnsureDefaultUriResult,
  ensureDefaultPlatformUri,
  resolveMissingDefaultUri,
} from './default-platform-uris'
import { hasDefaultPlatformUris, SIP_PLATFORMS, type SipPlatformKey } from './platform-config'
import { fetchOriginationURIsOrEmpty, useOriginationURIsQuery } from './queries'

// One provisioning run per platform+region at a time across every mounted
// section: the section remounts on a direction toggle and StrictMode runs
// effects twice, and either would otherwise race two creates of the same URI.
const inFlight = new Map<string, Promise<EnsureDefaultUriResult>>()

/** Test seam: forget in-flight runs between cases. */
export const resetDefaultUriEnsureCache = (): void => {
  inFlight.clear()
}

/**
 * Ensures the account owns a URI for the selected platform, creating the
 * platform default when it has none. Never blocks the form: a failure is one
 * toast and Create new URI still works.
 */
export const useEnsureDefaultPlatformUris = ({
  platform,
  enabled,
}: {
  platform: SipPlatformKey | undefined
  enabled: boolean
}): { isEnsuring: boolean; uris: ROriginationURIInternal[] | undefined } => {
  const queryClient = useQueryClient()
  const { data, isSuccess } = useOriginationURIsQuery(enabled)
  const existing = data?.data
  // A platform that already failed once this mount is not retried on every list
  // refetch; a platform whose defaults are all present costs nothing to re-check.
  const attempted = useRef(new Set<string>())
  const [isEnsuring, setIsEnsuring] = useState(false)

  useEffect(() => {
    if (!enabled || !platform || !hasDefaultPlatformUris(platform) || !isSuccess) return
    const region = getLocalAuth().region
    const key = `${platform}:${region ?? ''}`
    if (!resolveMissingDefaultUri(platform, region, existing ?? []).createPayload) return
    if (attempted.current.has(key)) return
    attempted.current.add(key)

    let run = inFlight.get(key)
    if (!run) {
      run = ensureDefaultPlatformUri({
        platform,
        region,
        listUris: fetchOriginationURIsOrEmpty,
        createUri: (payload) => settingsService.createZentrunkURI(payload),
      }).finally(() => inFlight.delete(key))
      inFlight.set(key, run)
    }

    // The invalidate below refetches the list, which re-runs this effect (and
    // exits early on `attempted`); loading must clear on settle regardless.
    setIsEnsuring(true)
    run
      .then(({ created, failed }) => {
        // List key only: the base prefix would also refetch every per-URI
        // sip_user lookup, and the new default never carries one.
        if (created) {
          queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().zentrunkUris })
        }
        if (failed) {
          errorToast({
            title: `Couldn't set up the default URI for ${SIP_PLATFORMS[platform].label}`,
            description: 'You can still add one with "Create new URI".',
          })
        }
      })
      .finally(() => setIsEnsuring(false))
  }, [enabled, platform, isSuccess, existing, queryClient])

  return { isEnsuring, uris: existing }
}
